// Calls for help (spec/05 §11, spec/13 §3.2; F-SES-25). A player raises a call from the station; the
// front of house sees it in "What needs you" and works it to done.
//
//   POST /v1/station/calls                              a player raises a call, station-authenticated
//   POST /v1/station/calls/current/cancel               the player cancels their open call
//   GET  …/venues/{venueId}/calls                        the venue's open and recent calls   calls:read
//   POST …/venues/{venueId}/calls/{callId}/acknowledge   "On my way"                          calls:manage
//   POST …/venues/{venueId}/calls/{callId}/resolve       "Done"                               calls:manage
//
// A call has a **state** it moves through: `open` → `acknowledged` → `resolved`, or `cancelled` (the
// player) or `expired` (nobody answered within `calls.expireMinutes`). A player has at most one call
// that is not closed at a time; a new one replaces it (spec/05 §11 rule 4).
//
// The lifecycle changes are pushed to the console over `venue:{v}:calls` (`calls.opened` /
// `calls.updated` / `calls.closed`, spec/09 §6.3) and to the station's call banner as a
// `station.call.updated` command (spec/09 §6.2). The console's REST list here is the recovery
// mechanism (spec/09 §4). The player-app push (`me.call.updated`) rides in with the player app.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PlayerIdSchema, SessionCallIdSchema, SessionIdSchema, StationIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { CALLS_MANAGE, CALLS_READ } from "../permissions/index.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its ' +
    'own organisation, venue and station from the token; the call is raised for the player in session on it.';
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/05 §11)
// ---------------------------------------------------------------------------------------------
/** What the player is calling about (spec/05 §11, spec/12 §10). Matches the kiosk's Call staff dialog. */
export const StationCallCategorySchema = z
    .enum(['pc', 'peripherals', 'food', 'other'])
    .openapi('StationCallCategory', { description: 'What a call for help is about (spec/05 §11).' });
/** Where a call is in its lifecycle (spec/05 §11). */
export const StationCallStateSchema = z
    .enum(['open', 'acknowledged', 'resolved', 'cancelled', 'expired'])
    .openapi('StationCallState', { description: 'Where a call for help is in its lifecycle (spec/05 §11).' });
export const StationCallSchema = z
    .object({
    id: SessionCallIdSchema,
    stationId: StationIdSchema,
    /** The session the call was raised from, or `null` when the station had none. */
    sessionId: SessionIdSchema.nullable(),
    /** The player who raised it, or `null` for a station with no signed-in player. */
    playerId: PlayerIdSchema.nullable(),
    category: StationCallCategorySchema,
    /** The player's optional note, up to 140 characters (spec/05 §11). */
    note: z.string().min(1).max(140).nullable(),
    state: StationCallStateSchema,
    raisedAt: IsoDateTimeSchema,
    /** When a staff member answered it, or `null` while it waits. */
    acknowledgedAt: IsoDateTimeSchema.nullable(),
    /** The name of the staff member on the way, shown on the kiosk ("Riya is on the way"); `null` while it waits. */
    acknowledgedByName: z.string().min(1).max(120).nullable(),
    /** When it was resolved, cancelled or expired, or `null` while it is still open. */
    closedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationCall', {
    description: 'A player’s call for help on a station, through its lifecycle (spec/05 §11, F-SES-25).',
});
export const StationCallListSchema = z
    .object({ calls: z.array(StationCallSchema) })
    .openapi('StationCallList', { description: 'A venue’s open and recently closed calls for help, newest first.' });
// ---------------------------------------------------------------------------------------------
// The station's own surface: raise and cancel (station-authenticated)
// ---------------------------------------------------------------------------------------------
export const RaiseCallSchema = z
    .object({
    category: StationCallCategorySchema,
    /** An optional note the player typed, up to 140 characters (spec/05 §11). */
    note: z.string().min(1).max(140).optional(),
})
    .openapi('RaiseCall');
export const raiseCallRoute = createRoute({
    method: 'post',
    path: '/v1/station/calls',
    operationId: 'raiseCall',
    tags: ['calls', 'stations'],
    summary: 'Raise a call for help (F-SES-25)',
    description: `${STATION_AUTHENTICATION} The player picks a category and an optional note; the call appears on the ` +
        'floor and in "What needs you" (spec/05 §11). A player’s previous open call is replaced.',
    request: {
        body: { content: { 'application/json': { schema: RaiseCallSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StationCallSchema, 'The raised call.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelCallRoute = createRoute({
    method: 'post',
    path: '/v1/station/calls/current/cancel',
    operationId: 'cancelCall',
    tags: ['calls', 'stations'],
    summary: 'Cancel the station’s open call for help (F-SES-25)',
    description: `${STATION_AUTHENTICATION} Cancels the station's open call — the player changed their mind. Answers 404 when there is none.`,
    responses: {
        200: jsonResponse(StationCallSchema, 'The cancelled call.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no open call.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The console's surface: list, acknowledge, resolve
// ---------------------------------------------------------------------------------------------
export const listCallsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/calls',
    operationId: 'listCalls',
    tags: ['calls'],
    summary: 'The venue’s calls for help (F-SES-25)',
    description: 'Every open call and the recently closed ones, newest first — what the console’s "What needs you" panel ' +
        'and the floor tile flags read (spec/13 §3.2). Requires `calls:read`; kept live over `venue:{v}:calls`.',
    request: { params: VenueScopeParamsSchema },
    [PERMISSION_EXTENSION]: CALLS_READ,
    responses: {
        200: jsonResponse(StationCallListSchema, 'The venue’s calls.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const CallActionParamsSchema = VenueScopeParamsSchema.extend({
    callId: SessionCallIdSchema,
}).openapi('CallActionParams');
export const acknowledgeCallRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/calls/{callId}/acknowledge',
    operationId: 'acknowledgeCall',
    tags: ['calls'],
    summary: 'Answer a call for help — "On my way" (F-SES-25)',
    description: 'A staff member takes the call: it moves to `acknowledged`, and the kiosk shows "{name} is on the way" ' +
        '(a `station.call.updated` command, spec/09 §6.2). Requires `calls:manage`. Audited.',
    request: { params: CallActionParamsSchema },
    [PERMISSION_EXTENSION]: CALLS_MANAGE,
    responses: {
        200: jsonResponse(StationCallSchema, 'The acknowledged call.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such call in this venue.', 'not-found'),
        409: problemResponse('The call is already closed.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const resolveCallRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/calls/{callId}/resolve',
    operationId: 'resolveCall',
    tags: ['calls'],
    summary: 'Resolve a call for help — "Done" (F-SES-25)',
    description: 'The staff member is finished: the call moves to `resolved` and clears from the station banner and the ' +
        'floor. Requires `calls:manage`. Audited.',
    request: { params: CallActionParamsSchema },
    [PERMISSION_EXTENSION]: CALLS_MANAGE,
    responses: {
        200: jsonResponse(StationCallSchema, 'The resolved call.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such call in this venue.', 'not-found'),
        409: problemResponse('The call is already closed.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const CALL_ROUTES = [
    raiseCallRoute,
    cancelCallRoute,
    listCallsRoute,
    acknowledgeCallRoute,
    resolveCallRoute,
];
//# sourceMappingURL=calls.js.map